import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { bareChatId, useConversation } from '../../hooks/useConversation';
import { applyConversationTitle } from '../../lib/chat/applyConversationTitle';
import { agentClient } from '../../lib/connect/clients';
import { toast } from '../toast';
/**
 * The chat header title, with inline rename for editors. Clicking the title
 * (or its pencil) swaps to an input; committing persists via
 * AgentService.UpdateConversation and optimistically updates the header +
 * history caches through applyConversationTitle (same write the streamed
 * title event uses). The description is preserved from the conversation cache.
 */ export function ChatTitle({ chatId, title, editable, persisted = true }) {
    const queryClient = useQueryClient();
    const conversation = useConversation(persisted ? chatId : '');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(title);
    const inputRef = useRef(null);
    const rename = useMutation({
        mutationFn: (next)=>agentClient.updateConversation({
                id: bareChatId(chatId),
                title: next,
                description: conversation.data?.conversation?.description ?? ''
            }),
        onMutate: (next)=>applyConversationTitle(queryClient, chatId, next),
        onError: ()=>{
            applyConversationTitle(queryClient, chatId, title); // revert optimistic
            toast({
                type: 'error',
                title: 'Could not rename chat'
            });
        }
    });
    useEffect(()=>{
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [
        editing
    ]);
    const commit = ()=>{
        setEditing(false);
        const next = draft.trim();
        if (next && next !== title) rename.mutate(next);
    };
    if (editing) {
        return /*#__PURE__*/ _jsx("input", {
            ref: inputRef,
            value: draft,
            onChange: (e)=>setDraft(e.target.value),
            onBlur: commit,
            onKeyDown: (e)=>{
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                } else if (e.key === 'Escape') {
                    setEditing(false);
                }
            },
            "aria-label": "Chat name",
            "data-testid": Testids.chatSettings.headerTitle,
            className: "min-w-0 flex-1 rounded border border-border-strong bg-transparent px-1.5 py-0.5 text-[13.5px] font-medium text-foreground outline-none"
        });
    }
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: ()=>{
            if (!editable) return;
            setDraft(title);
            setEditing(true);
        },
        disabled: !editable,
        "data-testid": Testids.chatSettings.headerTitle,
        title: editable ? 'Rename chat' : title,
        className: "group flex min-w-0 items-center gap-1.5 text-[13.5px] font-medium text-foreground disabled:cursor-default",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "truncate",
                children: title
            }),
            editable && /*#__PURE__*/ _jsx(Pencil, {
                className: "h-3.5 w-3.5 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
            })
        ]
    });
}
